import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, HelpCircle } from "lucide-react";
import { insuranceOptions } from "../../data/insuranceChoiceData";
import { useNavigate } from "react-router-dom";
import Images from "../../utils/Images";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";
import HeroSlider from "../../components/event_media/HeroSlider";

function OptionCard({ option, onClick }) {
  return (
    <motion.button
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="overflow-hidden relative text-left bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm transition-all duration-300 cursor-pointer group focus:outline-none hover:shadow-xl hover:ring-2 hover:ring-emerald-400 focus:ring-2 focus:ring-emerald-400"
    >
      <div className="flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 h-48 sm:h-56 md:h-64">
        <div className="flex flex-1 justify-center items-center min-h-0 mb-2 overflow-hidden">
          <img
            src={option.image}
            alt={option.title}
            className={`object-contain w-full h-full transition-all duration-300 ${option.key === "road-trip" ? "scale-150 group-hover:scale-[1.55]" : "group-hover:scale-105"}`}
          />
        </div>

        <div className="text-center">
          <h4 className="mb-1 text-sm font-semibold text-gray-900 sm:mb-1.5 sm:text-base md:mb-2 md:text-lg">
            {option.title}
          </h4>
          <p className="text-xs leading-tight text-gray-600 sm:text-xs md:text-sm md:leading-relaxed">
            {option.subtitle}
          </p>
        </div>

        <div className="absolute inset-0 bg-gradient-to-b transition-all duration-300 pointer-events-none from-emerald-50/0 via-emerald-50/0 to-emerald-50/0 group-hover:from-emerald-50/20 group-hover:via-emerald-50/10 group-hover:to-emerald-50/20" />
      </div>
    </motion.button>
  );
}

export default function InsuranceChoice() {
  const navigate = useNavigate();
  const [helperOpen, setHelperOpen] = useState(false);
  const [toast, setToast] = useState("");

  const handleCardClick = (opt) => {
    if (opt.comingSoon) {
      setToast("This item is coming soon");
      setTimeout(() => setToast(""), 1800);
      return;
    }

    // Navigate to the insurance hero page with the selected product
    if (opt.link) {
      navigate(opt.link);
    }
  };
  const heroImages = [
    Images.hero_img_web_three,
    Images.hero_img_web_four,
  ].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  return (
    <>
      <HeroSlider images={heroSlides}/>
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex gap-4 justify-between items-center mb-6">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              Choose Your Insurance
            </h2>
            <div className="mt-2 w-32 h-1 bg-emerald-500 rounded" />
          </div>

          <motion.button
            onClick={() => navigate('/dashboard')}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50"
          >
            <ChevronLeft size={16} /> Back to Dashboard
          </motion.button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4"
          >
            {insuranceOptions.map((opt) => (
              <OptionCard
                key={opt.key}
                option={opt}
                onClick={() => handleCardClick(opt)}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Helper bubble */}
        <motion.button
          onClick={() => setHelperOpen((v) => !v)}
          className="inline-flex fixed bottom-4 left-4 z-30 justify-center items-center w-12 h-12 text-white bg-gray-900 rounded-full shadow-lg sm:left-6 sm:bottom-6"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <HelpCircle size={20} />
        </motion.button>

        <AnimatePresence>
          {helperOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.25 }}
              className="fixed left-4 sm:left-6 bottom-16 sm:bottom-20 z-30 p-3 sm:p-4 w-80 sm:w-72 max-w-[calc(100vw-2rem)] sm:max-w-none text-xs sm:text-sm text-gray-700 bg-white rounded-xl border border-gray-200 shadow-xl"
            >
              <p className="mb-2 font-semibold text-gray-900">How it works</p>
              <ul className="pl-4 space-y-1 list-disc sm:pl-5">
                <li>Choose an insurance option to proceed.</li>
                <li>Items marked as Coming soon are not yet available.</li>
                <li>Select a registrant and complete the purchase flow.</li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              className="fixed bottom-6 left-1/2 z-30 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg -translate-x-1/2"
            >
              {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
    <AuthenticatedFooter />
    </>
  );
}

